/* ================================================================
   QR-Code — eigenständige Umsetzung, ohne Fremdpaket.

   Bewusst auf den hier gebrauchten Fall zugeschnitten: Byte-Modus,
   Fehlerkorrektur-Stufe M, Versionen 1 bis 6 (bis 106 Zeichen). Das
   deckt Freigabe-Links mit großem Abstand ab und spart die
   Sonderbehandlung, die Versionen ab 7 verlangen würden.

   qrMatrix(text) liefert eine Matrix aus true/false (true = dunkel)
   oder null, wenn der Text nicht hineinpasst.
   ================================================================ */

/* Datenwörter, EC-Wörter je Block und Blockanzahl — Stufe M. */
const VERSIONEN = [
  /* v1 */ { daten: 16, ecProBlock: 10, bloecke: 1 },
  /* v2 */ { daten: 28, ecProBlock: 16, bloecke: 1 },
  /* v3 */ { daten: 44, ecProBlock: 26, bloecke: 1 },
  /* v4 */ { daten: 64, ecProBlock: 18, bloecke: 2 },
  /* v5 */ { daten: 86, ecProBlock: 24, bloecke: 2 },
  /* v6 */ { daten: 108, ecProBlock: 16, bloecke: 4 },
];

/* Mittelpunkte der Ausrichtungsmuster je Version (v1 hat keine).
   Index ist version-1. */
const AUSRICHTUNG = [[], [6, 18], [6, 22], [6, 26], [6, 30], [6, 34]];

/* --- Galois-Feld GF(256), Generator 0x11d --------------------- */
const EXP = new Uint8Array(512);
const LOG = new Uint8Array(256);
(function feldAufbauen() {
  let x = 1;
  for (let i = 0; i < 255; i++) {
    EXP[i] = x;
    LOG[x] = i;
    x <<= 1;
    if (x & 0x100) x ^= 0x11d;
  }
  for (let i = 255; i < 512; i++) EXP[i] = EXP[i - 255];
})();

const mul = (a, b) => (a === 0 || b === 0) ? 0 : EXP[LOG[a] + LOG[b]];

/* Generatorpolynom für n Fehlerkorrekturwörter. */
function generator(n) {
  let g = [1];
  for (let i = 0; i < n; i++) {
    const neu = new Array(g.length + 1).fill(0);
    for (let j = 0; j < g.length; j++) {
      neu[j] ^= g[j];                      /* Verschiebung um x */
      neu[j + 1] ^= mul(g[j], EXP[i]);     /* konstantes Glied α^i */
    }
    g = neu;
  }
  return g;
}

/* Reed-Solomon: Restpolynom der Division durch den Generator. */
function ecWoerter(daten, anzahl) {
  const g = generator(anzahl);
  const rest = new Array(daten.length + anzahl).fill(0);
  for (let i = 0; i < daten.length; i++) rest[i] = daten[i];
  for (let i = 0; i < daten.length; i++) {
    const f = rest[i];
    if (f === 0) continue;
    for (let j = 0; j < g.length; j++) rest[i + j] ^= mul(g[j], f);
  }
  return rest.slice(daten.length);
}

/* --- Formatinformation (BCH 15,5 + Maske) -------------------- */
function formatBits(maske) {
  /* Stufe M entspricht den Bits 00. */
  let wert = (0b00 << 3) | maske;
  let rest = wert << 10;
  for (let i = 14; i >= 10; i--) {
    if ((rest >> i) & 1) rest ^= 0b10100110111 << (i - 10);
  }
  return ((wert << 10) | rest) ^ 0b101010000010010;
}

/* --- Text zu Bitstrom ---------------------------------------- */
function utf8Bytes(text) {
  const out = [];
  for (const zeichen of unescape(encodeURIComponent(text))) out.push(zeichen.charCodeAt(0));
  return out;
}

function datenWoerter(bytes, kapazitaet) {
  const bits = [];
  const schiebe = (wert, laenge) => {
    for (let i = laenge - 1; i >= 0; i--) bits.push((wert >> i) & 1);
  };
  schiebe(0b0100, 4);          /* Byte-Modus */
  schiebe(bytes.length, 8);    /* Längenfeld: 8 Bit bis Version 9 */
  for (const b of bytes) schiebe(b, 8);

  const maxBits = kapazitaet * 8;
  for (let i = 0; i < 4 && bits.length < maxBits; i++) bits.push(0);  /* Abschluss */
  while (bits.length % 8 !== 0) bits.push(0);

  const woerter = [];
  for (let i = 0; i < bits.length; i += 8) {
    let w = 0;
    for (let j = 0; j < 8; j++) w = (w << 1) | bits[i + j];
    woerter.push(w);
  }
  /* Auffüllen mit dem vorgeschriebenen Wechselmuster. */
  const fueller = [0xec, 0x11];
  let k = 0;
  while (woerter.length < kapazitaet) woerter.push(fueller[k++ % 2]);
  return woerter;
}

/* --- Feste Muster -------------------------------------------- */
function grundgeruest(groesse, version) {
  const m = Array.from({ length: groesse }, () => new Array(groesse).fill(null));
  const setze = (z, s, wert) => { m[z][s] = wert; };

  /* Suchmuster mit Trennlinie in den drei Ecken. */
  const sucher = (zTop, sLinks) => {
    for (let z = -1; z <= 7; z++) {
      for (let s = -1; s <= 7; s++) {
        const zz = zTop + z, ss = sLinks + s;
        if (zz < 0 || zz >= groesse || ss < 0 || ss >= groesse) continue;
        const rand = (z >= 0 && z <= 6 && (s === 0 || s === 6)) ||
                     (s >= 0 && s <= 6 && (z === 0 || z === 6));
        const kern = z >= 2 && z <= 4 && s >= 2 && s <= 4;
        setze(zz, ss, rand || kern);
      }
    }
  };
  sucher(0, 0); sucher(0, groesse - 7); sucher(groesse - 7, 0);

  /* Taktmuster. */
  for (let i = 8; i < groesse - 8; i++) {
    setze(6, i, i % 2 === 0);
    setze(i, 6, i % 2 === 0);
  }

  /* Ausrichtungsmuster — nicht über die Suchmuster legen. */
  const zentren = AUSRICHTUNG[version - 1];
  for (const z of zentren) {
    for (const s of zentren) {
      const nahSucher = (z <= 8 && s <= 8) || (z <= 8 && s >= groesse - 9) || (z >= groesse - 9 && s <= 8);
      if (nahSucher) continue;
      for (let dz = -2; dz <= 2; dz++) {
        for (let ds = -2; ds <= 2; ds++) {
          setze(z + dz, s + ds, Math.max(Math.abs(dz), Math.abs(ds)) !== 1);
        }
      }
    }
  }

  /* Dunkles Modul, immer gesetzt. */
  setze(groesse - 8, 8, true);
  return m;
}

/* Plätze der Formatinformation (werden später gefüllt). */
function formatPlaetze(groesse) {
  const a = [], b = [];
  for (let i = 0; i <= 5; i++) a.push([8, i]);
  a.push([8, 7], [8, 8], [7, 8]);
  for (let i = 9; i <= 14; i++) a.push([14 - i, 8]);
  for (let i = 0; i <= 7; i++) b.push([8, groesse - 1 - i]);
  for (let i = 8; i <= 14; i++) b.push([groesse - 15 + i, 8]);
  return [a, b];
}

/* --- Maskenbewertung ----------------------------------------- */
const MASKEN = [
  (z, s) => (z + s) % 2 === 0,
  (z) => z % 2 === 0,
  (z, s) => s % 3 === 0,
  (z, s) => (z + s) % 3 === 0,
  (z, s) => (Math.floor(z / 2) + Math.floor(s / 3)) % 2 === 0,
  (z, s) => ((z * s) % 2) + ((z * s) % 3) === 0,
  (z, s) => (((z * s) % 2) + ((z * s) % 3)) % 2 === 0,
  (z, s) => (((z + s) % 2) + ((z * s) % 3)) % 2 === 0,
];

function strafe(m) {
  const n = m.length;
  let punkte = 0;

  /* Regel 1: Ketten gleicher Farbe ab Länge 5. */
  const kette = (hole) => {
    for (let a = 0; a < n; a++) {
      let lauf = 1;
      for (let b = 1; b < n; b++) {
        if (hole(a, b) === hole(a, b - 1)) {
          lauf++;
        } else {
          if (lauf >= 5) punkte += lauf - 2;
          lauf = 1;
        }
      }
      if (lauf >= 5) punkte += lauf - 2;
    }
  };
  kette((z, s) => m[z][s]);
  kette((s, z) => m[z][s]);

  /* Regel 2: gleichfarbige 2×2-Blöcke. */
  for (let z = 0; z < n - 1; z++) {
    for (let s = 0; s < n - 1; s++) {
      const v = m[z][s];
      if (v === m[z][s + 1] && v === m[z + 1][s] && v === m[z + 1][s + 1]) punkte += 3;
    }
  }

  /* Regel 3: suchmusterähnliche Folgen. */
  const muster1 = [true, false, true, true, true, false, true, false, false, false, false];
  const muster2 = [false, false, false, false, true, false, true, true, true, false, true];
  const passt = (hole, a, b, muster) => {
    for (let i = 0; i < 11; i++) if (hole(a, b + i) !== muster[i]) return false;
    return true;
  };
  for (let z = 0; z < n; z++) {
    for (let s = 0; s <= n - 11; s++) {
      if (passt((a, b) => m[a][b], z, s, muster1)) punkte += 40;
      if (passt((a, b) => m[a][b], z, s, muster2)) punkte += 40;
      if (passt((a, b) => m[b][a], z, s, muster1)) punkte += 40;
      if (passt((a, b) => m[b][a], z, s, muster2)) punkte += 40;
    }
  }

  /* Regel 4: Abweichung vom ausgeglichenen Verhältnis. */
  let dunkel = 0;
  for (let z = 0; z < n; z++) for (let s = 0; s < n; s++) if (m[z][s]) dunkel++;
  const anteil = (dunkel * 100) / (n * n);
  punkte += Math.floor(Math.abs(anteil - 50) / 5) * 10;
  return punkte;
}

/* --- Hauptfunktion ------------------------------------------- */
function qrMatrix(text) {
  const bytes = utf8Bytes(String(text));

  /* Kleinste Version wählen, in die der Text passt. */
  let version = -1;
  for (let i = 0; i < VERSIONEN.length; i++) {
    if (bytes.length + 2 <= VERSIONEN[i].daten) { version = i + 1; break; }
  }
  if (version === -1) return null;

  const { daten: datenKapazitaet, ecProBlock, bloecke } = VERSIONEN[version - 1];
  const groesse = 17 + 4 * version;

  /* Datenwörter bilden und blockweise kodieren. */
  const alle = datenWoerter(bytes, datenKapazitaet);
  const proBlock = datenKapazitaet / bloecke;
  const datenBloecke = [], ecBloecke = [];
  for (let i = 0; i < bloecke; i++) {
    const teil = alle.slice(i * proBlock, (i + 1) * proBlock);
    datenBloecke.push(teil);
    ecBloecke.push(ecWoerter(teil, ecProBlock));
  }

  /* Verschränken: erst Daten spaltenweise, dann EC spaltenweise. */
  const folge = [];
  for (let i = 0; i < proBlock; i++) for (const b of datenBloecke) folge.push(b[i]);
  for (let i = 0; i < ecProBlock; i++) for (const b of ecBloecke) folge.push(b[i]);

  const bits = [];
  for (const w of folge) for (let i = 7; i >= 0; i--) bits.push((w >> i) & 1);

  /* Bits im Zickzack von rechts unten einsetzen. */
  const geruest = grundgeruest(groesse, version);
  const reserviert = geruest.map((zeile) => zeile.map((v) => v !== null));
  const [fmtA, fmtB] = formatPlaetze(groesse);
  for (const [z, s] of fmtA) reserviert[z][s] = true;
  for (const [z, s] of fmtB) reserviert[z][s] = true;

  const feld = geruest.map((zeile) => zeile.slice());
  let bitIndex = 0, aufwaerts = true;
  for (let spalte = groesse - 1; spalte > 0; spalte -= 2) {
    if (spalte === 6) spalte--;   /* Taktspalte überspringen */
    for (let i = 0; i < groesse; i++) {
      const zeile = aufwaerts ? groesse - 1 - i : i;
      for (let d = 0; d < 2; d++) {
        const s = spalte - d;
        if (reserviert[zeile][s]) continue;
        feld[zeile][s] = bitIndex < bits.length ? bits[bitIndex++] === 1 : false;
      }
    }
    aufwaerts = !aufwaerts;
  }

  /* Beste Maske wählen. */
  let besteMatrix = null, besterWert = Infinity;
  for (let maske = 0; maske < 8; maske++) {
    const m = feld.map((zeile) => zeile.slice());
    for (let z = 0; z < groesse; z++) {
      for (let s = 0; s < groesse; s++) {
        if (!reserviert[z][s] && MASKEN[maske](z, s)) m[z][s] = !m[z][s];
      }
    }
    const fmt = formatBits(maske);
    fmtA.forEach(([z, s], i) => { m[z][s] = ((fmt >> (14 - i)) & 1) === 1; });
    fmtB.forEach(([z, s], i) => { m[z][s] = ((fmt >> (14 - i)) & 1) === 1; });

    const wert = strafe(m);
    if (wert < besterWert) { besterWert = wert; besteMatrix = m; }
  }
  return besteMatrix.map((zeile) => zeile.map((v) => v === true));
}

export { qrMatrix };
